// src/components/pages/backtest/hooks/useBacktestData.js
import { useState, useEffect, useMemo, useCallback } from "react";
import { reconstructMap, generateColors, getRollingConfig, COLORS } from "../utils/backtestUtils";

// [추가] 히트맵 데이터 계산 헬퍼 함수
// [수정] 히트맵 데이터 계산 헬퍼 함수
const calculateHeatmapData = (dates, dailyReturnsMap, dailyDrawdownMap) => {
  const matrix = {};

  dates.forEach(dateStr => {
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = date.getMonth();

    if (!matrix[year]) {
      matrix[year] = {
        returns: Array(12).fill(0),
        drawdowns: Array(12).fill(0),
        dailyReturnAccumulators: Array(12).fill(1),
        hasData: Array(12).fill(false)
      };
    }

    // FIX: .get() 대신 객체 접근 [] 사용
    const dReturn = dailyReturnsMap[dateStr] || 0;
    const dDrawdown = dailyDrawdownMap[dateStr] || 0;

    matrix[year].dailyReturnAccumulators[month] *= (1 + dReturn);
    matrix[year].hasData[month] = true;

    if (dDrawdown < matrix[year].drawdowns[month]) {
      matrix[year].drawdowns[month] = dDrawdown;
    }
  });

  const result = {};
  Object.keys(matrix).forEach(year => {
    const yData = matrix[year];
    const monthlyReturns = yData.dailyReturnAccumulators.map((acc, i) => yData.hasData[i] ? acc - 1 : null);
    const yearTotal = yData.dailyReturnAccumulators.reduce((acc, curr, i) => yData.hasData[i] ? acc * curr : acc, 1) - 1;

    result[year] = {
      returns: monthlyReturns,
      drawdowns: yData.drawdowns,
      totalReturn: yearTotal
    };
  });

  return result;
};

export const useBacktestData = ({ stocks, period, benchmark, optimizedWeights, startDate, endDate, rebalancePeriod, t }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // --- 1. Data Fetching ---
  useEffect(() => {
    if (!stocks?.length || !optimizedWeights) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        let url = `/.netlify/functions/backtest?stocks=${stocks.join(',')}&benchmark=${benchmark}&weights=${encodeURIComponent(JSON.stringify(optimizedWeights))}&rebalance=${rebalancePeriod}`;
        url += (startDate && endDate) ? `&b_start=${startDate}&b_end=${endDate}` : `&b_period=${period}`;
       
        const response = await fetch(url);
        if (!response.ok) {
          const res = await response.json().catch(() => ({}));
          throw new Error(res.error || t("backtest.fetchErrorDetailed"));
        }

        const rawData = await response.json();
        const dates = rawData.portfolio.optimized.dates;

        // 데이터 복원 (Array -> Map)
        const pfReturns = reconstructMap(rawData.portfolio.optimized.dailyReturns, dates);
        const pfDrawdowns = reconstructMap(rawData.portfolio.optimized.dailyMaxDrawdown, dates);
        
        rawData.portfolio.optimized.dailyReturns = pfReturns;
        rawData.portfolio.optimized.dailyMaxDrawdown = pfDrawdowns;

        // [추가] 히트맵 데이터 생성
        rawData.portfolio.optimized.heatmap = calculateHeatmapData(dates, pfReturns, pfDrawdowns);

        if (rawData.benchmark) {
          rawData.benchmark.dailyReturns = reconstructMap(rawData.benchmark.dailyReturns, dates);
          rawData.benchmark.dailyMaxDrawdown = reconstructMap(rawData.benchmark.dailyMaxDrawdown, dates);
          // [✅ 추가됨] 벤치마크 히트맵 데이터 계산
          rawData.benchmark.heatmap = calculateHeatmapData(
            dates, 
            rawData.benchmark.dailyReturns, 
            rawData.benchmark.dailyMaxDrawdown
          );
        }

        if (rawData.individualStocks) {
          rawData.individualStocks = rawData.individualStocks.map(stock => ({
            ...stock,
            dailyReturns: reconstructMap(stock.dailyReturns, dates),
            dailyMaxDrawdown: reconstructMap(stock.dailyMaxDrawdown, dates)
          }));
        }
        setData(rawData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [stocks, optimizedWeights, period, benchmark, startDate, endDate, rebalancePeriod, t]);

  // --- 2. Chart Data Processing Helpers ---
  const dynamicColors = useMemo(() => 
    data?.individualStocks ? generateColors(data.individualStocks.length) : [], 
  [data]);

  const getWeightChartData = useCallback(() => {
    if (!data?.portfolio?.optimized?.dailyWeights) return { data: [], shapes: [] };
    
    const weightsObj = data.portfolio.optimized.dailyWeights;
    const dates = data.portfolio.optimized.dates || [];
    const rebalanceShapes = [];

    // 리밸런싱 선 계산
    if (dates.length > 1 && rebalancePeriod && rebalancePeriod !== 'none') {
      for (let i = 1; i < dates.length; i++) {
        const cur = new Date(dates[i]), prev = new Date(dates[i-1]);
        let isRebal = false;
        if (rebalancePeriod === 'monthly' && cur.getMonth() !== prev.getMonth()) isRebal = true;
        if (rebalancePeriod === 'quarterly' && Math.floor(cur.getMonth()/3) !== Math.floor(prev.getMonth()/3)) isRebal = true;
        if (rebalancePeriod === 'yearly' && cur.getFullYear() !== prev.getFullYear()) isRebal = true;

        if (isRebal) {
          rebalanceShapes.push({
             type: 'line', x0: dates[i], x1: dates[i], y0: 0, y1: 1, xref: 'x', yref: 'paper',
             line: { color: '#ff6b6b', width: 1.5, dash: 'dash' }
          });
        }
      }
    }

    const chartData = stocks.map((symbol, i) => ({
      x: dates,
      y: dates.map(date => (weightsObj[symbol]?.[date] !== undefined ? weightsObj[symbol][date] * 100 : 0)),
      name: symbol, type: 'scatter', mode: 'lines', stackgroup: 'one',
      line: { width: 0.5, color: dynamicColors[i % dynamicColors.length] },
      fillcolor: dynamicColors[i % dynamicColors.length]
    }));

    return { data: chartData, shapes: rebalanceShapes };
  }, [data, stocks, rebalancePeriod, dynamicColors]);

  // --- 3. Main Chart Processor ---
  const getProcessedChartData = useCallback((type, activeTab) => {
    if (!data) return { data: [], title: "" };
    const { portfolio, benchmark: bm, individualStocks } = data;

    // A. Cumulative
    if (type === 'cumulative') {
      if (activeTab.cumulative === 'individualStocks') {
        if (activeTab.portfolioView === 'difference') {
           return { data: individualStocks.map((s, i) => ({
             x: s.dates, y: s.cumulative.map((v, idx) => v - bm.cumulative[idx]),
             type: "scatter", mode: "lines", name: `${s.symbol} - ${bm.symbol}`, line: { color: dynamicColors[i] }
           }))};
        }
        return { data: [
          ...individualStocks.map((s, i) => ({ x: s.dates, y: s.cumulative, type: "scatter", mode: "lines", name: s.symbol, line: { color: dynamicColors[i] } })),
          { x: bm.dates, y: bm.cumulative, type: "scatter", mode: "lines", name: `BM (${bm.symbol})`, line: { color: COLORS.benchmark } }
        ]};
      }
      // Portfolio vs Benchmark
      if (activeTab.portfolioView === 'difference') {
        return { data: [{ x: portfolio.optimized.dates, y: portfolio.optimized.cumulative.map((v, i) => v - bm.cumulative[i]), type: "scatter", mode: "lines", name: t("backtest.differencePB"), line: { color: COLORS.diff } }] };
      }
      return { data: [
        { x: portfolio.optimized.dates, y: portfolio.optimized.cumulative, type: "scatter", mode: "lines", name: t("backtest.portfolio"), line: { color: COLORS.optimized } },
        { x: bm.dates, y: bm.cumulative, type: "scatter", mode: "lines", name: `BM (${bm.symbol})`, line: { color: COLORS.benchmark } }
      ]};
    }

    // B. Return / Drawdown
    const isReturn = type === 'return';
    const group = isReturn ? activeTab.returnGroup : activeTab.drawdownGroup;
    const view = isReturn ? activeTab.returnView : activeTab.drawdownView;
    const periodKey = isReturn ? activeTab.returnPeriod : activeTab.drawdownPeriod;
    const rollPeriod = isReturn ? activeTab.rollingPeriod : activeTab.rollingDrawdownPeriod;

    if (view === 'calendar') {
       const dataKey = `${periodKey}${isReturn ? 'Returns' : 'MaxDrawdown'}`;
       if (group === 'individualStocks') {
          const allKeys = Array.from(new Set(individualStocks.flatMap(s => Object.keys(s[dataKey] || {})))).sort();
          if (activeTab.portfolioView === 'difference') {
             return { data: individualStocks.map((s, i) => ({
               x: allKeys, y: allKeys.map(k => ((s[dataKey][k]||0) - (bm[dataKey][k]||0)) * 100),
               name: `${s.symbol} - ${bm.symbol}`, type: "bar", marker: { color: dynamicColors[i] }
             }))};
          }
          return { data: individualStocks.map((s, i) => ({
            x: allKeys, y: allKeys.map(k => (s[dataKey][k]||0) * 100),
            name: s.symbol, type: "bar", marker: { color: dynamicColors[i] }
          }))};
       } 
       // Pf vs Bm (Calendar)
       const keys = Object.keys(portfolio.optimized[dataKey]);
       if (activeTab.portfolioView === 'difference') {
         return { data: [{ x: keys, y: keys.map(k => (portfolio.optimized[dataKey][k] - bm[dataKey][k])*100), type: "bar", name: t("backtest.differencePB"), marker: { color: COLORS.diff } }] };
       }
       return { data: [
         { x: keys, y: Object.values(portfolio.optimized[dataKey]).map(v=>v*100), type: "bar", name: "Portfolio", marker: { color: COLORS.optimized } },
         { x: keys, y: Object.values(bm[dataKey]).map(v=>v*100), type: "bar", name: bm.symbol, marker: { color: COLORS.benchmark } }
       ]};
    } else {
       // Rolling
       const { suffix, days } = getRollingConfig(rollPeriod);
       const keyName = isReturn ? `rollingReturns${suffix}` : `rollingDrawdown${suffix}`;
       const xDates = portfolio.optimized.dates.slice(days);
       
       if (group === 'individualStocks') {
          if (activeTab.portfolioView === 'difference') {
             return { data: individualStocks.map((s, i) => ({
               x: xDates, y: (s[keyName] || []).map((v, idx) => (v - (bm[keyName][idx] || 0)) * 100),
               name: `${s.symbol} - ${bm.symbol}`, type: "scatter", mode: "lines", line: { color: dynamicColors[i] }
             }))};
          }
          return { data: [
             ...individualStocks.map((s, i) => ({ x: xDates, y: (s[keyName]||[]).map(v=>v*100), type: "scatter", mode: "lines", name: s.symbol, line: { color: dynamicColors[i] } })),
             { x: xDates, y: (bm[keyName]||[]).map(v=>v*100), type: "scatter", mode: "lines", name: bm.symbol, line: { color: COLORS.benchmark } }
          ]};
       }
       // Pf vs Bm (Rolling)
       const pRoll = portfolio.optimized[keyName] || [], bRoll = bm[keyName] || [];
       if (activeTab.portfolioView === 'difference') {
          return { data: [{ x: xDates, y: pRoll.map((v, i) => (v - bRoll[i]) * 100), type: "scatter", mode: "lines", name: t("backtest.differencePB"), line: { color: COLORS.diff } }] };
       }
       return { data: [
          { x: xDates, y: pRoll.map(v => v * 100), type: "scatter", mode: "lines", name: "Portfolio", line: { color: COLORS.optimized } },
          { x: xDates, y: bRoll.map(v => v * 100), type: "scatter", mode: "lines", name: bm.symbol, line: { color: COLORS.benchmark } }
       ]};
    }
  }, [data, stocks, benchmark, t, dynamicColors]);

  return { backtestData: data, loading, error, getWeightChartData, getProcessedChartData };
};